import {
  enforceLenient,
  privilegesLenient,
  AbacReducedPolicy,
  AbacRuleComparison,
} from '@lifeomic/abac';

export interface Rule {
  [rule: string]: AbacRuleComparison;
}

export interface Policy {
  rules: Record<string, boolean | Rule[]>;
}

/** Policy from Redux (`evaluatedPolicy`). */
export type EvaluatedPolicy = Policy | AbacReducedPolicy | null | undefined;

export const enforce = (
  operation: string,
  policy: AbacReducedPolicy,
  attributes?: object,
) => {
  if (!policy) {
    return false;
  } else {
    return enforceLenient(operation, policy, attributes || {});
  }
};

export const privileges: typeof privilegesLenient = (policy, attributes) => {
  if (!policy) {
    return [];
  } else {
    return privilegesLenient(policy, attributes);
  }
};

export const permissions = {
  accessAdmin: 'accessAdmin',
  billingAdmin: 'billingAdmin',
  projectAdmin: 'projectAdmin',
  accountAdmin: 'accountAdmin',
  ehrAdmin: 'ehrAdmin',
  createData: 'createData',
  readData: 'readData',
  readMaskedData: 'readMaskedData',
  updateData: 'updateData',
  deleteData: 'deleteData',
  downloadFile: 'downloadFile',
  layoutAdmin: 'layoutAdmin',
  apiKeyUser: 'apiKeyUser',
  ruleAdmin: 'ruleAdmin',
  publishContent: 'publishContent',
  inviteUsers: 'inviteUsers',
  developApps: 'developApps',
  ingestFromEHR: 'ingestFromEHR',
  fileAdmin: 'fileAdmin',
  workflowAdmin: 'workflowAdmin',
  createGenomicData: 'createGenomicData',
  clinicalTrialAlertUser: 'clinicalTrialAlertUser',
  createSubjectLayoutNote: 'createSubjectLayoutNote',
  employeeAdmin: 'employeeAdmin',
};

export const hasAccessToProject = (policy: Policy, project: any) => {
  const attributes = {
    resource: {
      dataset: project.id,
    },
  };

  return (
    enforce(permissions.readData, policy, attributes) ||
    enforce(permissions.readMaskedData, policy, attributes) ||
    enforce(permissions.downloadFile, policy, attributes)
  );
};

/**
 * ABAC `resource.name` for Zori clinical (members) KB files — must match provisioned rules
 * (prefix `.ai-kb-members/...` in file-service).
 */
export const AI_KB_MEMBERS_RESOURCE_NAME = '.ai-kb-members' as const;

/**
 * ABAC `resource.name` for Zori internal KB files (prefix `.ai-kb-internal/...`).
 */
export const AI_KB_INTERNAL_RESOURCE_NAME = '.ai-kb-internal' as const;

export type AiKbResourceName =
  | typeof AI_KB_MEMBERS_RESOURCE_NAME
  | typeof AI_KB_INTERNAL_RESOURCE_NAME;

/** Attributes for one Zori KB path prefix in the dataset (FLDM-1421 rules are prefix-specific). */
const aiKbFileAttributes = (projectId: string, name: AiKbResourceName) => ({
  resource: {
    type: 'file',
    dataset: projectId,
    name,
  },
});

/** ABAC: user may read files under this KB prefix (readData / readMaskedData / downloadFile). */
export const canReadAiKbPath = (
  policy: EvaluatedPolicy,
  projectId: string,
  kbResourceName: AiKbResourceName,
): boolean => {
  if (!policy || !projectId) {
    return false;
  }
  const attributes = aiKbFileAttributes(projectId, kbResourceName);
  return (
    enforce(permissions.readData, policy, attributes) ||
    enforce(permissions.readMaskedData, policy, attributes) ||
    enforce(permissions.downloadFile, policy, attributes)
  );
};

/** ABAC: user may create/update/delete files under this KB prefix. */
export const canUpdateAiKbPath = (
  policy: EvaluatedPolicy,
  projectId: string,
  kbResourceName: AiKbResourceName,
): boolean => {
  if (!policy || !projectId) {
    return false;
  }
  const attributes = aiKbFileAttributes(projectId, kbResourceName);
  return (
    enforce(permissions.updateData, policy, attributes) ||
    enforce(permissions.createData, policy, attributes) ||
    enforce(permissions.deleteData, policy, attributes)
  );
};

/**
 * Will check if the user has readMaskedData permission but not read data permission.
 * If a cohort ID is provided, it is possible that a user is mask forced for the project
 * but has readData for the active cohort and the user is not forced into masking.
 */
export const isMaskingForced = (
  policy: Policy,
  project: string,
  cohort?: string,
) => {
  // first check explicitely without cohorts
  const withoutCohortsAttributes = {
    resource: {
      dataset: project,
    },
  };

  const withUnspecifiedCohortAttributes = {
    resource: {
      dataset: project,
      cohorts: ['unspecified'],
      cohortAffinity: true,
    },
  };

  const forceMaskedForJustProject =
    enforce(permissions.readMaskedData, policy, withoutCohortsAttributes) &&
    !enforce(permissions.readData, policy, withoutCohortsAttributes);

  const forceMaskedForUnspecifiedCohort =
    enforce(
      permissions.readMaskedData,
      policy,
      withUnspecifiedCohortAttributes,
    ) &&
    !enforce(permissions.readData, policy, withUnspecifiedCohortAttributes);

  const projectLevelMaskForced =
    forceMaskedForJustProject || forceMaskedForUnspecifiedCohort;

  // if no cohort specified, no need to evaluate if user has cohort access
  if (!cohort) return projectLevelMaskForced;

  // if the user is not masked forced at the project level, then cohort check is irrelevant
  if (!projectLevelMaskForced) return false;

  const withCohortsAttributes = {
    resource: {
      dataset: project,
      cohorts: [cohort],
      cohortAffinity: true,
    },
  };

  return (
    enforce(permissions.readMaskedData, policy, withCohortsAttributes) &&
    !enforce(permissions.readData, policy, withCohortsAttributes)
  );
};

/**
 * Whether the user may re-assign member consents in PHC for the active project/cohort
 * and subject. Aligned with member-ops assign-consents / member contact editing
 * (`updateData` on dataset + subject; cohort context matches `useEnforce` / FL policy).
 */
export const canReassignMemberConsent = (
  policy: EvaluatedPolicy,
  projectId: string | undefined,
  cohortId: string | null | undefined,
  patientId: string | undefined,
): boolean => {
  if (!policy || !projectId || !patientId) {
    return false;
  }
  const resource = {
    dataset: projectId,
    subject: patientId,
    cohorts: cohortId ? [cohortId] : ['unspecified'],
    cohortAffinity: true,
  };
  return enforce(permissions.updateData, policy, { resource });
};
